import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { CalendarDays, MapPin, CheckCircle2, XCircle } from "lucide-react";
import { pApi, errMsg, fmtDate, fmtClock } from "../lib/api";
import { Loading } from "../components/common";

const STATE = { confirmed: "You have CONFIRMED that you will attend. Thank you!", cancelled: "Your pre-registration is CANCELLED. Thank you for letting us know.", registered: "Please tell us if you will still attend." };

export default function RsvpLink() {
  const { token } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { pApi.get(`/rsvp-link/${token}`).then((r) => setD(r.data)).catch((e) => setErr(errMsg(e))); }, [token]);
  const act = async (action) => {
    setBusy(true);
    try { setD((await pApi.post(`/rsvp-link/${token}`, { action })).data); } catch (e) { setErr(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen bg-[#FDFBF7] px-4 py-10 flex justify-center">
      <div className="w-full max-w-lg min-w-0 grid gap-5 content-start">
        <p className="text-xs uppercase tracking-[0.2em] text-[#8A6D3B]">Saint Francis of Assisi Parish · Attendance confirmation</p>
        {err && <p className="rounded-xl bg-red-50 border border-red-200 p-4 text-red-800" data-testid="rsvp-link-error">{err} <Link className="underline font-semibold" to="/me">Go to My Profile</Link></p>}
        {!d && !err && <Loading />}
        {d && <RsvpCard d={d} busy={busy} act={act} />}
      </div>
    </div>
  );
}

function RsvpCard({ d, busy, act }) {
  const { meeting: m, record: r } = d;
  return (
    <div className="sfap-card p-6 grid gap-4 min-w-0" data-testid="rsvp-link-card">
      <div>
        <h1 className="font-serif text-3xl font-black text-[#2D3B2D] break-words" data-testid="rsvp-link-title">{m.title}</h1>
        <p className="text-sm text-stone-500 font-mono">{m.code}</p>
      </div>
      <div className="grid gap-1 text-sm">
        <span className="flex items-center gap-2"><CalendarDays className="h-4 w-4" />{fmtDate(m.date)} · {fmtClock(m.start_time)}</span>
        {m.venue && <span className="flex items-center gap-2"><MapPin className="h-4 w-4" />{m.venue}</span>}
      </div>
      <p>Dear <b>{d.name}</b>,</p>
      {r.waitlisted && r.rsvp !== "cancelled" && <p className="rounded-xl bg-amber-50 border border-amber-300 p-3 text-amber-900 text-sm font-semibold" data-testid="rsvp-link-waitlisted">You are on the WAITLIST. You will get a seat automatically if someone cancels.</p>}
      <p className="font-semibold" data-testid="rsvp-link-status">{STATE[r.rsvp] || STATE.registered}</p>
      {d.can_change ? (
        <div className="grid sm:grid-cols-2 gap-3">
          {r.rsvp !== "confirmed" && <button className="btn-big btn-primary" disabled={busy} onClick={() => act("confirm")} data-testid="rsvp-link-confirm-btn"><CheckCircle2 className="h-5 w-5" />Yes, I will attend</button>}
          <button className="btn-big btn-ghost" disabled={busy} onClick={() => act("cancel")} data-testid="rsvp-link-cancel-btn"><XCircle className="h-5 w-5" />I can no longer attend</button>
        </div>
      ) : r.rsvp !== "cancelled" && <p className="text-sm text-stone-500" data-testid="rsvp-link-locked">This pre-registration can no longer be changed online.</p>}
    </div>
  );
}
